import React, { useMemo, useEffect, useRef } from "react";
import { StylesConfig } from "react-select";
import { createTimesheetModal, HourModel } from "../model/timesheet";
import { useReportTimesheetContext } from "../context/ReportTimsheetContex";
import { daysDataEnum, ReportStatusEnum } from "../helper/constant";
export const CustomSelectStyles1: StylesConfig = {
  control: (provided) => ({
    ...provided,
    textAlign: "left",
    border: "0 solid", // Removes the border
    borderRadius: "0.5rem",
    padding: "2px", // Adjust padding as needed
    boxShadow: "none", // Removes the default box shadow
    "&:hover": {
      borderColor: "initial", // Adjust hover border color if needed
    },
    "&:focus": {
      borderColor: "initial", // Adjust focus border color if needed
      boxShadow: "none", // Removes the default focus box shadow
    },
  }),
};

interface TableRowProps {
  row: createTimesheetModal;
  mainIndex: number;
  timesheetData?: createTimesheetModal[];
  updateRow: (
    id: number,
    fields: string,
    value: string | number | HourModel[]
  ) => void;
  removeRow?: (id: number) => void;
  showRemove?: boolean;
  daysData: string[];
  report_statuses_id: boolean;
  userType: string;
  report_id: number | null;
  rejectedDates: number[] | null;
  previewFlag: boolean;
}

const CustomTableRow: React.FC<TableRowProps> = ({
  mainIndex,
  row,
  updateRow,
  userType,
  timesheetData,
  daysData,
  report_statuses_id,
  report_id,
  rejectedDates,
  previewFlag,
}) => {
  const { activity } = useReportTimesheetContext();

  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

  const autoResizeDescription = () => {
    const el = descriptionRef.current;
    if (!el) return;

    if (previewFlag) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
      return;
    } else {
      const style = window.getComputedStyle(el);
      let lineHeight = parseFloat(style.lineHeight);
      if (Number.isNaN(lineHeight)) {
        const fontSize = parseFloat(style.fontSize);
        lineHeight = Number.isNaN(fontSize) ? 20 : fontSize * 1.5;
      }
      const paddingTop = parseFloat(style.paddingTop);
      const paddingBottom = parseFloat(style.paddingBottom);
      const borderTop = parseFloat(style.borderTopWidth);
      const borderBottom = parseFloat(style.borderBottomWidth);
      const maxHeight =
        lineHeight * 4 + paddingTop + paddingBottom + borderTop + borderBottom;
      el.style.height = "auto";
      const newHeight = Math.min(el.scrollHeight, maxHeight);
      el.style.height = `${newHeight}px`;

      el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
    }
  };

  useEffect(() => {
    autoResizeDescription();
  }, [row.task_description]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    let value = e.target.value;
    // Remove non-numeric characters except '.'
    value = value.replace(/[^0-9.]/g, "");

    if (parseInt(value) > 24) {
      return;
    }
    // Ensure that there are only up to two digits before the decimal point

    const parts = value.split(".");

    if (parts.length > 0 && parts[1] && parseInt(parts[1]) > 59) {
      return;
    }

    if (parts[0].length > 2) {
      parts[0] = parts[0].slice(0, 2); // Limit integer part to two digits
    }

    const integerPart = parseInt(parts[0], 10);

    // Prevent values greater than 24
    if (integerPart > 24) {
      return;
    }

    // Ensure that there are one or two digits after the decimal point
    if (parts.length > 1) {
      parts[1] = parts[1].slice(0, 2); // Limit fractional part to two digits
    }

    value = parts.join("."); // Reassemble the number

    // Additional check to prevent values greater than 24.00
    if (integerPart === 24 && parseFloat(value) > 24.0) {
      return;
    }

    const newHours = [...row.days_data];
    newHours[index] = { ...newHours[index], hours: value };
    updateRow(mainIndex, "days_data", newHours);
  };

  const handleBlur = (index: number) => {
    let value = row.days_data[index].hours;

    if (value === " " || value === "") {
      return;
    }

    // If the value starts with a dot, prepend '0'
    if (value.startsWith(".")) {
      value = "0" + value;
    }

    // Split the value into integer and decimal parts
    let [integerPart, decimalPart] = value.split(".");

    // Ensure the integer part is limited to two digits
    if (integerPart.length > 2) {
      integerPart = integerPart.slice(0, 2);
    }

    // Process the decimal part
    if (decimalPart) {
      if (decimalPart.length > 2) {
        decimalPart = decimalPart.slice(0, 2);
      } else if (decimalPart.length === 1) {
        decimalPart = "0" + decimalPart; // Ensure two decimal places
      }
    } else {
      // If there's no decimal part and value has a single digit, add ".00"
      if (integerPart.length === 1) {
        decimalPart = "00";
      } else {
        decimalPart = "00";
      }
    }

    // Reassemble the number
    value = `${integerPart}.${decimalPart}`;

    const newHours = [...row.days_data];
    newHours[index] = { ...newHours[index], hours: value };
    updateRow(mainIndex, "days_data", newHours);
  };

  const calculateTotalHours = () => {
    return row.days_data.reduce(
      (total, { hours }) => {
        const [hoursInt, minutesInt] = hours.split(".").map(Number);
        total.hours += hoursInt;
        total.minutes += minutesInt || 0; // Handle cases where minutesInt might be NaN
        return total;
      },
      { hours: 0, minutes: 0 }
    );
  };

  const total = calculateTotalHours();
  const totalHours = total.hours + Math.floor(total.minutes / 60);
  const totalMinutes = total.minutes % 60;

  const selectedActivityIds = (timesheetData ?? []).map(
    (row) => row.project_activity_ids
  );

  // Filter out the already selected activity IDs for the current row
  const availableActivities = activity?.filter(
    (item) =>
      !selectedActivityIds.includes(item.id) ||
      item.id === row.project_activity_ids
  );

  const focusDayInput = (index: number) => {
    if (
      report_id === ReportStatusEnum.Approved ||
      !report_statuses_id ||
      userType === "manager"
    ) {
      return;
    }
    const el = document.getElementById(`cvv-input-${mainIndex}-${index}`) as
      | HTMLInputElement
      | null;
    el?.focus();
  };

  // Memoize the rendering of the <td> elements
  const renderedDays = useMemo(() => {
    return row.days_data.map((day, index) => (
      <td
        key={index}
        onClick={() => focusDayInput(index)}
        className={`px-0 py-0 cursor-text ${
          report_id !== ReportStatusEnum.Approved &&
          rejectedDates?.includes(parseInt(day.date.split("-")[0], 10))
            ? "bg-red-50"
            : // : report_id !== ReportStatusEnum.Approved && day.rejected
            // ? "bg-red-50"
            index === 0
            ? "bg-indigo-50 text-gray-900"
            : row.days_data.length - 1 === index
            ? "bg-indigo-50 text-gray-900"
            : daysData.includes(day.date)
            ? "bg-green-50"
            : day.day_types_id === daysDataEnum.HOLIDAY
            ? "bg-green-50"
            : "bg-white"
        }`}
      >
        {/* {parseInt(day.date.split("-")[0], 10)} */}
        {report_id !== ReportStatusEnum.Approved ? (
          <input
            type="text"
            value={day.hours}
            minLength={0}
            maxLength={5}
            className={`w-full h-full px-0 py-0 border-0 text-center text-gray-900 rounded-none focus:outline-none focus:ring-0 ${
              report_id !== ReportStatusEnum.Approved &&
              rejectedDates?.includes(parseInt(day.date.split("-")[0], 10))
                ? "bg-red-50"
                : // : report_id !== ReportStatusEnum.Approved && day.rejected
                // ? "bg-red-50"
                index === 0
                ? "bg-indigo-50 text-gray-900"
                : row.days_data.length - 1 === index
                ? "bg-indigo-50 text-gray-900"
                : daysData.includes(day.date)
                ? "bg-green-50"
                : day.day_types_id === daysDataEnum.HOLIDAY
                ? "bg-green-50"
                : "bg-white"
            }`}
            disabled={!report_statuses_id || userType === "manager"}
            id={`cvv-input-${mainIndex}-${index}`}
            onChange={(e) => handleInputChange(e, index)}
            onBlur={() => handleBlur(index)}
            required
          />
        ) : (
          day.hours
        )}
      </td>
    ));
  }, [row.days_data, daysData, report_statuses_id, rejectedDates, report_id, mainIndex]);

  const focusProjectSelect = () => {
    if (
      report_id === ReportStatusEnum.Approved ||
      !report_statuses_id ||
      userType === "manager"
    )
      return;
    const el = document.getElementById(
      `project-select-${mainIndex}`
    ) as HTMLSelectElement | null;
    if (el) {
      el.focus();
      el.click();
    }
  };

  return (
    <tr className="border  text-center">
      <td
        className="bg-white px-0 py-0 dark:bg-white w-64 md:w-80 cursor-pointer"
        onClick={focusProjectSelect}
      >
        {report_id !== ReportStatusEnum.Approved ? (
          <select
            name="project_activity_ids"
            value={row.project_activity_ids}
            disabled={!report_statuses_id || userType === "manager"}
            id={`project-select-${mainIndex}`}
            onChange={(e) =>
              updateRow(mainIndex, "project_activity_ids", e.target.value)
            }
            className="bg-white dark:bg-white ring-0 text-gray-900 text-sm block w-full p-2.5 "
          >
            <option key="placeholder" value="">Select</option>
            {row.project_activity_ids &&
              !availableActivities?.some(
                (item) => item?.id === row.project_activity_ids
              ) && (
              <option
                value={row.project_activity_ids}
                key={`selected-${row.project_activity_ids}`}
              >
                {activity?.find((item) => item.id === row.project_activity_ids)
                  ?.name || "Select"}
              </option>
            )}
            {availableActivities
              ?.filter((item) => item && item.id != null)
              .map((item) => (
                <option key={`avail-${item.id}`} value={item.id}>
                  {item.name}
                </option>
              ))}
          </select>
        ) : (
          row.project_activity_ids && (
            <p
              className="bg-white text-left text-gray-700 px-3 w-full h-auto py-0"
              key={row.project_activity_ids}
            >
              {activity?.find((item) => item.id === row.project_activity_ids)
                ?.name || "Select"}
            </p>
          )
        )}
      </td>
      <td className="px-0 py-0 w-auto h-auto">
        {report_id !== ReportStatusEnum.Approved ? (
          <textarea
            rows={1}
            maxLength={200}
            ref={descriptionRef}
            value={row.task_description}
            disabled={!report_statuses_id || userType === "manager"}
            onChange={(e) => {
              updateRow(mainIndex, "task_description", e.target.value);
              autoResizeDescription();
            }}
            className="shadow-sm bg-white border-0 border-gray-300 focus:outline-none focus:ring-0 text-gray-900 text-sm block w-full h-auto resize-none m-0 p-2.5 "
            style={{ height: "auto", overflowY: "hidden" }}
          />
        ) : (
          <p className="bg-white text-left text-gray-700 px-3 py-2">
            {" "}
            {row.task_description}{" "}
          </p>
        )}
      </td>
      {renderedDays}
      <td className="px-3 py-2 text-gray-900 bg-gray-100 ">{`${totalHours}:${totalMinutes
        .toString()
        .padStart(2, "0")}`}</td>
    </tr>
  );
};

export default CustomTableRow;
